// Справка „История на маси" (Справки → bills-history).
//
// Съдържанието е подредено като кутийки: лента с показателите за целия период,
// хистограма по час на затваряне и карта със списъка. Списъкът стои върху
// споделената таблица (src/components/dataTable) в СЪРВЪРЕН режим — сортиране,
// филтри в колоните и страниране се вдигат до `GET /bills/history`, а редът
// „Общо" идва от `totals` в същия отговор (страницата не може да сумира
// период, който не е зареден).

import { useEffect, useMemo, useState } from "react";
import { App } from "antd";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

import { useViewport } from "../../../hooks/useViewport";
import { useBillsHistory } from "../../../hooks/useReports";
import { useUsers } from "../../../hooks/useUsers";
import { usePaymentMethods } from "../../../hooks/usePaymentMethods";
import { ReportDatePicker } from "../../../components/ReportDatePicker";
import { useReportsDateParam } from "../useReportsDateParam";
import {
  DataTable,
  parseDecimal,
  readPersistedPageSize,
  readPersistedSorting,
} from "../../../components/dataTable";
import type { ColumnFilterRule, SortRule } from "../../../components/dataTable";
import type {
  BillHistory,
  BillsHistoryFilters,
  BillsHistorySortBy,
  BillsHistorySortOrder,
} from "../../../api/reportsBillsHistory";
import ExportExcelButton from "./ExportExcelButton";
import BillOperationsModal from "./BillOperationsModal";
import { ReportTopBar } from "./ReportTopBar";
import { previousPeriodOf } from "./reportPeriod";
import { BillsHistoryKpis } from "./billsHistory/BillsHistoryKpis";
import { BillsHistoryMobileCard } from "./billsHistory/BillsHistoryMobileCard";
import { useBillsHistoryColumns } from "./billsHistory/billsHistoryColumns";
import type { NamedBillHistory } from "./billsHistory/billsHistoryColumns";
import {
  businessDateOf,
  businessDayEndFrom,
  businessDayStartFrom,
} from "../../../lib/businessDay";
import {
  exportBillsHistory,
  fetchBillsForExport,
} from "./billsHistory/billsHistoryExport";

/** Търсачката чака да спреш да пишеш, преди да пита сървъра. */
const SEARCH_DEBOUNCE_MS = 400;
/** Таванът на `page_size` в BE — по-голям връща 422. */
const MAX_PAGE_SIZE = 200;
/** Ключът, под който таблицата пази подредбата на колоните на потребителя. */
const BILLS_HISTORY_TABLE_ID = "bills-history";
/** Подредбата, с която справката тръгва, когато няма запомнена. */
const DEFAULT_SORTING: SortRule[] = [{ id: "created_at", desc: true }];
/**
 * Ключовете, по които BE-то може да сортира (`BillsHistorySortBy`). Запомнено
 * сортиране по колона извън списъка се изхвърля — иначе `sort_by` връща 422.
 */
const SERVER_SORTABLE_IDS: readonly string[] = [
  "bill_id",
  "name",
  "status",
  "created_at",
  "closed_at",
  "duration_minutes",
  "total_orders",
  "cancelled_items",
  "payment_method_name",
  "receipt_number",
  "total_amount",
  "fiscal_amount",
  "total_discount",
  "items_quantity",
  "cancelled_total",
  "guests",
];

type Props = { onOpenMobileMenu?: () => void; isNarrow?: boolean };

export const BillsHistoryReport = ({
  onOpenMobileMenu,
  isNarrow = false,
}: Props = {}) => {
  const { t, i18n } = useTranslation();
  const { isNarrow: viewportNarrow } = useViewport();
  const narrow = isNarrow || viewportNarrow;
  const { message } = App.useApp();

  const { data: users = [] } = useUsers();
  const { data: paymentMethods = [] } = usePaymentMethods();

  // Датата живее и в query параметъра ?date= — пази се при смяна на справка.
  const [dateRange, setDateRange] = useReportsDateParam(
    () => [businessDayStartFrom(dayjs()), businessDayEndFrom(dayjs())],
    "business",
  );
  // Изключено по подразбиране: справката отговаря първо на въпроса „колко
  // е сега", а Δ-тата са втори въпрос и струват втора заявка за период,
  // който човек не е поискал.
  const [compare, setCompare] = useState(false);
  // „Само с фактура" — сметките с АКТИВЕН ред във фактура (`in_invoice`).
  // Бутонът задава въпроса на целия период с едно натискане; колоната
  // „Фактура" пита същото поле, но през фунията си (виж `invoiceFilter`).
  const [invoicedOnly, setInvoicedOnly] = useState(false);
  // Изолиране на ПРИКЛЮЧЕНИТЕ сметки без номер на бон — точно разликата между
  // двата режима на видимост. Пали се с клик върху втората група в кутийката
  // „Приключени"; категорията не се назовава никъде.
  const [isolateMarked, setIsolateMarked] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  // Запомненото сортиране влиза И в заявката, не само в стрелката на хедъра.
  const [sorting, setSorting] = useState<SortRule[]>(() =>
    readPersistedSorting(
      BILLS_HISTORY_TABLE_ID,
      DEFAULT_SORTING,
      SERVER_SORTABLE_IDS,
    ),
  );
  const [columnFilters, setColumnFilters] = useState<ColumnFilterRule[]>([]);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(() =>
    readPersistedPageSize(BILLS_HISTORY_TABLE_ID, 20),
  );
  const [selectedBill, setSelectedBill] = useState<BillHistory | null>(null);

  useEffect(() => {
    const timer = setTimeout(
      () => setDebouncedSearch(searchText.trim()),
      SEARCH_DEBOUNCE_MS,
    );
    return () => clearTimeout(timer);
  }, [searchText]);

  // Смяна на периода или на текста връща на първа страница — иначе човек стои
  // на страница 7 от набор, който вече има три реда.
  useEffect(() => {
    setPageIndex(0);
  }, [dateRange, debouncedSearch, invoicedOnly, isolateMarked]);

  const selectedOf = (columnId: string): string[] => {
    const rule = columnFilters.find((filter) => filter.id === columnId);
    return Array.isArray(rule?.value) ? (rule.value as string[]) : [];
  };

  const waiterSelection = selectedOf("waiter");
  const paymentSelection = selectedOf("payment_method_name");
  const invoiceSelection = selectedOf("is_invoiced");

  // Колоната „Фактура" се вдига до едно-единствено булево (`in_invoice`),
  // затова има смисъл само когато е отметната ТОЧНО ЕДНА от двете стойности.
  // И двете (или нито една) = въпросът не стеснява нищо → без параметър.
  const invoiceFilter =
    invoiceSelection.length === 1 ? invoiceSelection[0] === "true" : undefined;

  // Тези стесняват СПИСЪКА, но не и `summary` (BE смята показателите по
  // период + сервитьор + право). Виж бележката в `BillsHistoryKpis`.
  const narrowsListOnly =
    paymentSelection.length > 0 ||
    invoicedOnly ||
    invoiceFilter !== undefined ||
    isolateMarked ||
    debouncedSearch !== "";

  const isMultiDay = useMemo(() => {
    if (!dateRange?.[0] || !dateRange?.[1]) return false;
    // По РАБОТНА дата: краят на работния ден е 05:59 на следващата календарна
    // дата и суровото сравнение обявява един ден за период.
    return !businessDateOf(dateRange[0]).isSame(
      businessDateOf(dateRange[1]),
      "day",
    );
  }, [dateRange]);

  const filters = useMemo<BillsHistoryFilters>(() => {
    const next: BillsHistoryFilters = {
      page: pageIndex + 1,
      page_size: Math.min(pageSize, MAX_PAGE_SIZE),
      context: "reports",
      date_field: "closed_at",
      // Справката е за ПРИКЛЮЧЕНИ сметки. Отворената маса още не е сметка —
      // няма закриване, няма бон и няма как да участва в сверка.
      statuses: ["closed"],
    };
    if (dateRange?.[0] && dateRange?.[1]) {
      next.start_date = dateRange[0].format("YYYY-MM-DD HH:mm:ss");
      next.end_date = dateRange[1].format("YYYY-MM-DD HH:mm:ss");
    }
    if (sorting.length) {
      next.sort_by = sorting[0].id as BillsHistorySortBy;
      next.sort_order = (
        sorting[0].desc ? "desc" : "asc"
      ) as BillsHistorySortOrder;
    }
    if (isolateMarked) next.has_receipt = false;
    if (waiterSelection.length) next.waiter_ids = waiterSelection.map(Number);
    if (paymentSelection.length) {
      next.payment_method_ids = paymentSelection.map(Number);
    }
    if (debouncedSearch) next.q = debouncedSearch;
    // Бутонът и фунията на колоната пълнят ЕДИН И СЪЩ параметър. Натиснатият
    // бутон е по-силният: докато свети, филтърът на колоната не може да го
    // отмени — иначе „Само с фактура" би показвало нефактурирани сметки.
    if (invoicedOnly) next.in_invoice = true;
    else if (invoiceFilter !== undefined) next.in_invoice = invoiceFilter;
    return next;
  }, [
    pageIndex,
    pageSize,
    dateRange,
    sorting,
    invoicedOnly,
    invoiceFilter,
    isolateMarked,
    waiterSelection,
    paymentSelection,
    debouncedSearch,
  ]);

  // Предходният период за делтите — със `summary_only`, за да не се зарежда
  // втори път целият списък със сметките и редовете им. Краят му е подрязан до
  // изтеклото време, когато избраният период още тече (виж `reportPeriod`).
  const previousFilters = useMemo<BillsHistoryFilters | null>(() => {
    if (!compare || !dateRange?.[0] || !dateRange?.[1]) return null;
    const previous = previousPeriodOf(dateRange[0], dateRange[1]);
    return {
      ...filters,
      page: 1,
      page_size: 1,
      summary_only: true,
      start_date: previous.start.format("YYYY-MM-DD HH:mm:ss"),
      end_date: previous.end.format("YYYY-MM-DD HH:mm:ss"),
    };
  }, [compare, dateRange, filters]);

  const { data, isLoading, isFetching, refetch } = useBillsHistory(filters);
  const { data: previousData } = useBillsHistory(previousFilters ?? filters, {
    enabled: previousFilters !== null,
  });

  const sessionAccess = data?.session_access ?? true;
  const fiscalSplit = data?.fiscal_split ?? false;

  // Без втори код колоната „№ сметка" не съществува (виж
  // `useBillsHistoryColumns`), а запомнено сортиране по нея иначе би
  // продължило да отива в `sort_by` без стрелка в нито един хедър.
  useEffect(() => {
    if (sessionAccess) return;
    setSorting((previous) =>
      previous.some((rule) => rule.id === "bill_id")
        ? DEFAULT_SORTING
        : previous,
    );
  }, [sessionAccess]);

  const columns = useBillsHistoryColumns({
    users,
    paymentMethods,
    isMultiDay,
    sessionAccess,
    fiscalSplit,
  });

  const waiterNameById = useMemo(() => {
    const map = new Map<number, string>();
    for (const user of users) {
      map.set(user.id, user.names || user.username || String(user.id));
    }
    return map;
  }, [users]);

  const paymentNameById = useMemo(() => {
    const isEnglish = i18n.language?.startsWith("en");
    const map = new Map<number, string>();
    for (const method of paymentMethods) {
      map.set(method.id, isEnglish ? method.name_en : method.name_bg);
    }
    return map;
  }, [paymentMethods, i18n.language]);

  // Имената влизат в САМИТЕ редове, а не в `accessorFn`: TanStack кешира
  // стойността на клетката за живота на `data`, а React Query пази същата
  // референция при непроменен отговор — справочник, който пристига по-късно
  // (потребители, методи на плащане), иначе никога не се появява в клетката.
  const namedItems = useMemo<NamedBillHistory[]>(
    () =>
      (data?.items ?? []).map((bill) => ({
        ...bill,
        waiterName:
          bill.waiter_id !== null
            ? (waiterNameById.get(bill.waiter_id) ?? "")
            : "",
        paymentName:
          bill.payment_method_id !== null
            ? (paymentNameById.get(bill.payment_method_id) ??
              bill.payment_method_name ??
              "")
            : (bill.payment_method_name ?? ""),
      })),
    [data?.items, waiterNameById, paymentNameById],
  );

  const totals = useMemo(() => {
    const source = data?.totals;
    if (!source) return undefined;
    return {
      items_quantity: parseDecimal(source.items_quantity),
      total_discount: parseDecimal(source.discount_total),
      cancelled_total: parseDecimal(source.cancelled_total),
      total_amount: parseDecimal(source.amount_total),
      // По-старо BE без полето → „Сума ФП" = „Сума"
      fiscal_amount: parseDecimal(
        source.fiscal_amount_total ?? source.amount_total,
      ),
      guests: source.guests_total ?? 0,
    };
  }, [data?.totals]);

  const rowCount = data?.total ?? 0;

  const handleExport = async () => {
    try {
      const rows = await fetchBillsForExport(filters, rowCount);
      exportBillsHistory(rows, {
        t,
        waiterNameById,
        paymentNameById,
        fiscalSplit,
      });
    } catch {
      message.error(t("reportsPage.exportFailed"));
    }
  };

  return (
    <div className="flex h-full flex-col bg-canvas">
      <ReportTopBar
        title={t("reportsPage.billsHistoryTitle")}
        isNarrow={narrow}
        onOpenMobileMenu={onOpenMobileMenu}
        onRefresh={() => refetch()}
        isFetching={isFetching}
      />

      <div
        className={`min-h-0 flex-1 overflow-y-auto overscroll-contain ${
          narrow ? "px-3.5 pb-6 pt-3.5" : "px-7 pb-8 pt-[22px]"
        }`}
      >
        <div className="flex flex-col gap-3.5">
          <div className="flex flex-wrap items-center justify-center gap-2 overflow-x-auto pb-0.5">
            <ReportDatePicker
              dayMode="business"
              value={dateRange}
              onChange={setDateRange}
              defaultMode="day"
              withTime
            />
            <button
              type="button"
              onClick={() => setCompare((previous) => !previous)}
              aria-pressed={compare}
              className={`h-9 shrink-0 rounded-lg border px-3.5 text-[12.5px] font-bold transition-colors ${
                compare
                  ? "border-brand-tint2 bg-brand-tint text-brand-deep-ink"
                  : "border-shade-3 bg-surface text-soft hover:bg-fill"
              }`}
            >
              {compare ? "✓ " : ""}
              {t("reportsPage.billsHistoryComparePrevious")}
            </button>
            <button
              type="button"
              onClick={() => setInvoicedOnly((previous) => !previous)}
              aria-pressed={invoicedOnly}
              className={`h-9 shrink-0 rounded-lg border px-3.5 text-[12.5px] font-bold transition-colors ${
                invoicedOnly
                  ? "border-brand-tint2 bg-brand-tint text-brand-deep-ink"
                  : "border-shade-3 bg-surface text-soft hover:bg-fill"
              }`}
            >
              {invoicedOnly ? "✓ " : ""}
              {t("reportsPage.billsHistoryInvoicedOnly")}
            </button>
          </div>

          <BillsHistoryKpis
            summary={data?.summary}
            previousSummary={previousData?.summary}
            compare={compare}
            sessionAccess={sessionAccess}
            narrowed={narrowsListOnly}
            onIsolateMarked={() => setIsolateMarked((previous) => !previous)}
            isolatedMarked={isolateMarked}
            isNarrow={narrow}
          />

          <DataTable<NamedBillHistory>
            tableId={BILLS_HISTORY_TABLE_ID}
            data={namedItems}
            columns={columns}
            getRowId={(bill) => String(bill.id)}
            mode="server"
            server={{
              rowCount,
              pageIndex,
              onPageIndexChange: setPageIndex,
              onPageSizeChange: setPageSize,
              onSortingChange: setSorting,
              onColumnFiltersChange: setColumnFilters,
              totals,
            }}
            loading={isLoading}
            refreshing={isFetching && !isLoading}
            ariaLabel={t("reportsPage.billsHistoryTitle")}
            title={
              <div className="flex min-w-0 flex-col gap-1">
                <span className="font-pos-mono text-[10.5px] font-bold uppercase leading-none tracking-[0.1em] text-muted">
                  {t("reportsPage.billsHistoryTableKicker")}
                </span>
                <span className="text-[16px] font-extrabold leading-none text-ink">
                  {rowCount === 1
                    ? t("reportsPage.billsHistoryTableCountOne", {
                        count: rowCount,
                      })
                    : t("reportsPage.billsHistoryTableCount", {
                        count: rowCount,
                      })}
                  <span className="font-medium text-muted">
                    {` · ${t("reportsPage.billsHistoryTableHint")}`}
                  </span>
                </span>
              </div>
            }
            search={{
              value: searchText,
              onChange: setSearchText,
              placeholder: t("reportsPage.billsHistorySearchPlaceholder"),
            }}
            toolbarRight={
              <ExportExcelButton
                onExport={handleExport}
                disabled={rowCount === 0}
                bare
              />
            }
            onRowClick={setSelectedBill}
            emptyTitle={t("reportsPage.billsHistoryNoResults")}
            emptyHint={t("reportsPage.billsHistoryEmptyHint")}
            pageSize={pageSize}
            pageSizeOptions={[10, 20, 50, 100, 200]}
            mobileCard={(bill) => (
              <BillsHistoryMobileCard
                bill={bill}
                sessionAccess={sessionAccess}
                waiterName={
                  bill.waiter_id !== null
                    ? (waiterNameById.get(bill.waiter_id) ?? "")
                    : ""
                }
                paymentName={
                  bill.payment_method_id !== null
                    ? (paymentNameById.get(bill.payment_method_id) ??
                      bill.payment_method_name ??
                      "")
                    : (bill.payment_method_name ?? "")
                }
              />
            )}
            initialSorting={DEFAULT_SORTING}
          />
        </div>
      </div>

      {selectedBill ? (
        <BillOperationsModal
          bill={selectedBill}
          open
          onClose={() => setSelectedBill(null)}
          onBillUpdated={() => refetch()}
          showCancelButton
        />
      ) : null}
    </div>
  );
};
